import React from 'react';
import { Block } from 'baseui/block';
import { FormControl } from 'baseui/form-control';
import { Select, SIZE as SSIZE } from 'baseui/select';
import { Notification, KIND } from 'baseui/notification';
import { Spinner } from 'baseui/icon';
import { LabelSmall } from 'baseui/typography';
import { useModelRunSelection } from '../../hooks/useModelRunSelection';

/**
 * Reusable ModelRun selector component with automatic asset fetching
 * @param {Object} props - Component props
 * @param {string} props.modelerId - The modeler ID
 * @param {boolean} props.fetchAssets - Whether to fetch assets when ModelRun is selected
 * @param {boolean} props.fetchDetails - Whether to fetch full ModelRun details
 * @param {number} props.assetPageSize - Page size for asset fetching
 * @param {Function} props.onModelRunSelect - Callback when ModelRun is selected
 * @param {Function} props.onError - Callback when error occurs
 * @param {string} props.label - Label for the form control
 * @param {string} props.placeholder - Placeholder text for select
 * @param {boolean} props.disabled - Whether the select is disabled
 * @param {string} props.size - Size of the select component
 * @param {boolean} props.clearable - Whether the select is clearable
 * @param {boolean} props.showAssetCount - Whether to show asset count
 * @param {boolean} props.showLoadingState - Whether to show loading states
 * @param {boolean} props.showError - Whether to show error messages
 * @returns {JSX.Element} ModelRun selector component
 */
function ModelRunSelectorComponent({
  modelerId,
  fetchAssets = true,
  fetchDetails = false,
  assetPageSize = 100,
  onModelRunSelect = null,
  onError = null,
  label = "Select Model Run",
  placeholder = "Choose a model run...",
  disabled = false,
  size = SSIZE.medium,
  clearable = true,
  showAssetCount = true,
  showLoadingState = true,
  showError = true,
  ...otherProps
}) {
  const {
    selectedModelRunId,
    selectedModelRunData,
    modelRunAssets,
    loading,
    fetchingAssets,
    error,
    modelRunOptions,
    handleModelRunSelect,
    clearSelection
  } = useModelRunSelection({
    modelerId,
    fetchAssets,
    fetchDetails,
    assetPageSize,
    onModelRunSelect,
    onError
  });

  // Prepare selected value for the Select component
  const selectedValue = selectedModelRunId 
    ? modelRunOptions.filter(option => option.id === selectedModelRunId)
    : [];

  return (
    <Block>
      <FormControl label={label}>
        <Select
          size={size}
          options={modelRunOptions}
          value={selectedValue}
          onChange={handleModelRunSelect}
          placeholder={placeholder}
          clearable={clearable}
          disabled={disabled || loading}
          isLoading={loading}
          {...otherProps}
        />
      </FormControl>

      {/* Loading State */}
      {showLoadingState && (fetchingAssets || loading) && (
        <Block marginTop="scale300" display="flex" alignItems="center">
          <Spinner size={16} />
          <LabelSmall marginLeft="scale200">
            {fetchingAssets ? 'Loading assets...' : 'Loading model run details...'}
          </LabelSmall>
        </Block>
      )}

      {/* Asset Count Display */}
      {showAssetCount && selectedModelRunData && modelRunAssets.length > 0 && (
        <Block marginTop="scale300">
          <LabelSmall>
            {modelRunAssets.length} asset{modelRunAssets.length !== 1 ? 's' : ''} found
          </LabelSmall>
        </Block>
      )}

      {/* Error Display */}
      {showError && error && (
        <Block marginTop="scale300">
          <Notification kind={KIND.negative} closeable>
            {error}
          </Notification>
        </Block>
      )}

      {/* Selected ModelRun Info */}
      {selectedModelRunData && (
        <Block marginTop="scale300" padding="scale300" backgroundColor="backgroundTertiary">
          <LabelSmall>
            <strong>Selected:</strong> {selectedModelRunData.runName} 
            {selectedModelRunData.runStatus && ` (${selectedModelRunData.runStatus})`}
          </LabelSmall>
          {selectedModelRunData.runNotes && (
            <LabelSmall marginTop="scale200">
              <strong>Notes:</strong> {selectedModelRunData.runNotes}
            </LabelSmall>
          )}
        </Block>
      )}
    </Block>
  );
}

export default ModelRunSelectorComponent; 